import {inject, Injectable} from '@angular/core';
import {Router, UrlTree} from '@angular/router';
import {catchError} from 'rxjs/operators';
import {lastValueFrom, of} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ResponseCodeService} from './response-code.service';
import moment from 'moment';
import {DialogPosition, MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {DialogData} from "../common/interfaces/global-interfaces";
import {ConfirmationDialogComponent} from "../shared/components/confirmation-dialog/confirmation-dialog.component";
import Swal, {SweetAlertInput} from 'sweetalert2';


@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  dialogConfig = new MatDialogConfig();
  classes: string[] = [];

  dialog = inject(MatDialog);


  constructor(
    private router: Router,
    private snackbar: MatSnackBar,
    private responseCodeService: ResponseCodeService
  ) {
  }

  determineSnackbar(data: { code: string | number; description: string; }) {
    if (data.code === 9000) {
      this.successSnackbar(data?.description);
    } else if (data.code === 9002 ||
      data.code === 9003 ||
      data.code === 9004 ||
      data.code === 9005 ||
      data.code === 9006 ||
      data.code === 9007 ||
      data.code === 9008 ||
      data.code === 9009 ||
      data.code === 9010 ||
      data.code === 9011 ||
      data.code === 9012 ||
      data.code === 9013 ||
      data.code === 9012 ||
      data.code === 9014 ||
      data.code === 9015 ||
      data.code === 9016 ||
      data.code === 9017 ||
      data.code === 9018 ||
      data.code === 9019 ||
      data.code === 9020 ||
      data.code === 9021 ||
      data.code === 9022 ||
      data.code === 9023) {
      this.errorSnackbar(data?.description + ': ' + data?.code);
    } else {
      this.warningSnackbar(data?.description + ': ' + data?.code);
    }
  }

  confirmationSnackbar(message: string, action?: string) {
    return this.snackbar.open(message, action, {
      duration: 2500,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'green-snackbar'
    })
  }

  inputDialogue(
    message = 'Fill in Input',
    input: SweetAlertInput = 'text',
    text = 'Input',
    cancelButtonText = 'cancel'
  ) {
    return Swal.fire({
      title: message,
      icon: 'question',
      cancelButtonText,
      text,
      input,
      showCancelButton: true,
    });
  }

  confirmation(input?: DialogData, config?: {
    width?: string,
    panelClass?: string[],
    position?: DialogPosition,
    height?: string
  }) {
    if (config?.panelClass?.length > 0) {
      this.classes.push(...config?.panelClass);
    }

    let width = config?.width ? config?.width : '20%';

    if (input.isForm) {
      width = '35%';
    }
    const customClasses = this.classes?.length > 0 ? this.classes : ['confirmation-modal-dialog'];
    this.dialogConfig.disableClose = true;
    this.dialogConfig.autoFocus = true;
    this.dialogConfig.width = width;
    this.dialogConfig.data = input;
    this.dialogConfig.panelClass = customClasses;
    if (config?.position != null) {
      this.dialogConfig.position = config?.position;
    }
    if (config?.height != null) {
      this.dialogConfig.height = config?.height;
    }

    return lastValueFrom(
      this.dialog.open(ConfirmationDialogComponent, this.dialogConfig).afterClosed()
    );
  }

  successSnackbar(message: string, action?: string) {
    return this.snackbar.open(message, action, {
      duration: 2500,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: 'green-snackbar'
    });
  }

  errorSnackbar(message: string, action?: string) {
    return this.snackbar.open(message, action, {
      duration: 2500,
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: 'red-snackbar',
    });
  }

  warningSnackbar(message: string, action?: string) {
    return this.snackbar.open(message, action, {
      duration: 2500,
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: 'warning-snackbar'
    });
  }

  internetSnackbar(message: string, action?: string) {
    return this.snackbar.open(message, action, {
      duration: 2500,
      verticalPosition: 'top',
      panelClass: 'warning-snackbar'
    });
  }

  successMessage(message: string, duration?: number, action?: string, closeDialog: boolean = true) {
    if (closeDialog) this.dialog.closeAll()
    return this.snackbar.open(message, action, {
      duration: duration ? duration : 2500,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: 'green-snackbar'
    });
  }

  errorMessage(message: string, action?: string, duration?: number) {
    return this.snackbar.open(message, action, {
      duration: duration ?? 7000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: 'red-snackbar'
    });
  }

  successMessageWithRedirect(message: string, route: string | UrlTree) {
    const response = this.snackbar.open(message, '', {
      duration: 3000,
      horizontalPosition: 'center',
      panelClass: 'green-snackbar'
    });

    if (response) {
      return this.router.navigateByUrl(route);
    }
    return null;
  }

  catchError(message: string, action?: string) {
    return catchError((error) => {
      this.snackbar.open(message, action, {
        duration: 2500,
        verticalPosition: 'top',
        panelClass: 'red-snackbar'
      });
      return of([]);
    });
  }

  handleErrorMessage(data: any) {
    const responseCode = this.responseCodeService.getCodeDescription(
      data?.code
    );
    const message =
      responseCode[0]?.code + ' : ' + responseCode[0]?.description;
    return this.errorMessage(message);
  }

  formatDate(dateReceived: any, format: string = 'YYYY-MM-DD') {
    if (typeof dateReceived === 'string' && dateReceived !== '') {
      dateReceived = new Date(dateReceived);
    }
    return dateReceived ? moment(dateReceived).format(format) : undefined;
  }

  formatDateTime(dateReceived: any, format: string = 'YYYY-MM-DD HH:MM') {
    if (typeof dateReceived === 'string' && dateReceived !== '') {
      dateReceived = new Date(dateReceived);
    }
    return dateReceived ? moment(dateReceived).format(format) : undefined;
  }

  // formatDate(date: string | number | Date, format: string = 'dd/MM/yyyy'): string {
  //   return new Date(date).toISOString().substring(0, 10);
  //   // return new Date(date).toLocaleString().substring(0, 10);
  // }

}
